import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { ButtonPrimitive } from '@/components/ui/button-primitive'

export interface StepDef {
  key: string
  title: string
  navigable?: boolean
}

export type StepsColor = 'primary' | 'amber' | 'rose' | 'violet'

// `current` carries the `aria-[current=step]:` variant so the active style lives on the className
// (driven by the `aria-current="step"` attribute) instead of a JS conditional inside `cn`.
const colorClasses: Record<StepsColor, { current: string; completed: string; completedHover: string }> = {
  primary: {
    current: 'aria-[current=step]:bg-primary',
    completed: 'bg-primary/60',
    completedHover: 'hover:bg-primary/80',
  },
  amber: {
    current: 'aria-[current=step]:bg-amber-500',
    completed: 'bg-amber-500/60',
    completedHover: 'hover:bg-amber-500/80',
  },
  rose: {
    current: 'aria-[current=step]:bg-rose-500',
    completed: 'bg-rose-500/60',
    completedHover: 'hover:bg-rose-500/80',
  },
  violet: {
    current: 'aria-[current=step]:bg-violet-500',
    completed: 'bg-violet-500/60',
    completedHover: 'hover:bg-violet-500/80',
  },
}

export default function Steps(props: {
  steps: StepDef[]
  stepIndex: number
  onStepClick?: (index: number) => void
  className?: string
  color?: StepsColor
}) {
  const color = props.color ?? 'primary'
  const palette = colorClasses[color]
  return (
    <TooltipProvider delay={150}>
      <div className={cn('mx-auto flex w-full max-w-sm items-center gap-1.5', props.className)}>
        {props.steps.map((step, index) => {
          const isCompleted = index < props.stepIndex
          const isCurrent = index === props.stepIndex
          // `navigable: true` forces clickable regardless of completion (e.g. a step the user
          // has already finished out-of-order). Falls back to isCompleted for the legacy default.
          const canNavigate =
            !!props.onStepClick && step.navigable !== false && (step.navigable === true || isCompleted)

          return (
            <Tooltip key={step.key}>
              <TooltipTrigger
                render={
                  <ButtonPrimitive
                    type="button"
                    disabled={!canNavigate}
                    onClick={() => canNavigate && props.onStepClick?.(index)}
                    aria-label={`${index + 1}. ${step.title}`}
                    aria-current={isCurrent ? 'step' : undefined}
                    className={cn(
                      'group h-1.5 flex-1 rounded-full transition-colors disabled:cursor-default',
                      palette.current,
                      canNavigate && 'cursor-pointer',
                      isCompleted ? cn(palette.completed, palette.completedHover) : 'bg-muted',
                    )}
                  />
                }
              />
              <TooltipContent side="top">{step.title}</TooltipContent>
            </Tooltip>
          )
        })}
      </div>
    </TooltipProvider>
  )
}
